import {
  OfferingTypeKind,
  OfferingTypeScope,
  type OfferingType,
} from "@/features/church/config/offering-types/types/offering-type.types"

export function generateOfferingTypeId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }

  return `off_${Math.random().toString(36).slice(2, 10)}`
}

function normalizeName(name: string) {
  return name.trim().toLowerCase()
}

export function hasDuplicateName(
  offeringTypes: OfferingType[],
  name: string,
  scope: OfferingTypeScope,
  ownerBranchId?: string,
  ignoreId?: string
) {
  const normalized = normalizeName(name)
  return offeringTypes.some(
    (offeringType) =>
      offeringType.id !== ignoreId &&
      offeringType.scope === scope &&
      (scope === OfferingTypeScope.PARISH || offeringType.ownerBranchId === ownerBranchId) &&
      normalizeName(offeringType.name) === normalized
  )
}

function validateTemporaryDates(startDate?: string, endDate?: string) {
  const hasStart = Boolean(startDate)
  const hasEnd = Boolean(endDate)

  if (hasStart !== hasEnd) {
    throw new Error("errors.temporaryDatesRequiredTogether")
  }

  if (!hasStart || !hasEnd) {
    return
  }

  const start = new Date(startDate as string)
  const end = new Date(endDate as string)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error("errors.invalidDates")
  }

  if (end < start) {
    throw new Error("errors.endDateBeforeStart")
  }
}

export function sanitizeOfferingTypeInput(input: {
  scope: OfferingTypeScope
  ownerBranchId?: string
  kind: OfferingTypeKind
  linkedProgramId?: string
  startDate?: string
  endDate?: string
}) {
  const isTemporary = input.kind === OfferingTypeKind.TEMPORARY
  const isBranchScope = input.scope === OfferingTypeScope.BRANCH

  const ownerBranchId = isBranchScope ? input.ownerBranchId?.trim() || undefined : undefined
  if (isBranchScope && !ownerBranchId) {
    throw new Error("errors.ownerBranchRequired")
  }

  const linkedProgramId = isTemporary ? input.linkedProgramId?.trim() || undefined : undefined
  const startDate = isTemporary ? input.startDate?.trim() || undefined : undefined
  const endDate = isTemporary ? input.endDate?.trim() || undefined : undefined

  validateTemporaryDates(startDate, endDate)

  return {
    ownerBranchId,
    linkedProgramId,
    startDate,
    endDate,
  }
}

export function emulateDelay() {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, 220)
  })
}
